import {Checkbox, Divider, Typography} from '@mui/material';
import './AssignUsers.scss';
import {t} from 'i18next';
import ArrIconButton from '../../../components/common/arr-icon-button/ArrIconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {union, differenceBy} from 'lodash';
import {ReactComponent as CheckedIcon} from '../../../../src/assets/images/icon-checked.svg';
import {ReactComponent as UnCheckedIcon} from '../../../../src/assets/images/icon-unchecked.svg';
import {ReactComponent as InactiveIcon} from '../../../../src/assets/images/icon-unchecked-inactive.svg';
import ArrTransferList, {
  KeyValue,
} from '../../common/arr-transfer-list/ArrTransferList';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../shared/types';
import ArrMultiSelectDropdown from '../../common/arr-select-dropdown/ArrMultiSelectDropdown';
import {
  useDomainUsers,
  useProjectUsers,
} from '../../../hooks/Projects/useProjects';
import {ISecurityGroup, UserMappingItem} from '../../../api/types/project.type';
import {useDispatch, useSelector} from 'react-redux';
import {
  setAssignedAndUnassignedUsers,
  setBillableFlag,
  setClearState,
  setClearTabData,
  setRoleDropdownId,
  setSecurityGroupDropdownIds,
  setSecurityGroupList,
  setTypeDropdownId,
} from '../../../redux/slice/AssignUsersSlice';
import {RootState} from '../../../redux/store';

interface IManageUsers {
  unAssignedUsersForDomain: UserMappingItem[];
  unAssignedUsers: UserMappingItem[];
  domains: UserMappingItem[];
  projectsList: UserMappingItem[];
  rolesList: UserMappingItem[];
}
interface IAssignUsersContent {
  title: string;
  type: string;
  manageUsersData: IManageUsers | null;
  isLoader?: boolean;
}

const transformAssignUsers = (
  item: UserMappingItem,
  isBillable: boolean,
  role: ISelectItem | null,
  securityGroupSelected: ISelectItem[],
  type: string
) => {
  let output = '';
  const isDomain = type === 'domain';

  if (isDomain) {
    output = `${item.value} || IsBillable: ${isBillable}`;
  } else {
    const roleName = role?.name || '';
    const showSecurityGroup = role?.id !== 1 && securityGroupSelected?.length;
    const securityGroupText = showSecurityGroup
      ? ` || SecurityGroup: ${securityGroupSelected.map((group) => group.name).join(', ')}`
      : '';

    output = `${item.value} || Role: ${roleName}${securityGroupText} || IsBillable: ${isBillable}`;
  }

  return {
    text: output,

    key: null,
    value:
      type === 'domain'
        ? `${item.key}|||${isBillable}`
        : `${item.key}|${role?.id}|${securityGroupSelected?.map((group) => group.id).join(', ') || ''}|${isBillable}`,
    isSelected: item.isSelected,
  };
};

const transformUnassignUsers = (transformedItem: {
  value: string;
  text?: string;
  isSelected: boolean;
}) => {
  const [keyStr] = transformedItem.value.split('|||');
  const textValue = transformedItem?.text?.split(' || ')[0] ?? '';

  return {
    key: parseInt(keyStr, 10),
    value: textValue,
    isSelected: transformedItem.isSelected,
  };
};

const AssignUsersContent: React.FC<IAssignUsersContent> = ({
  title,
  type,
  manageUsersData,
  isLoader = false,
}) => {
  const dispatch = useDispatch();
  const domain = useSelector((state: RootState) => state.assignUser.domain);
  const project = useSelector((state: RootState) => state.assignUser.project);

  const clearState = useSelector(
    (state: RootState) => state.assignUser.clearState
  );

  const defaultAssignedUsers =
    type === 'domain' ? domain.assignedUsers : project.assignedUsers;
  const defaultUnassignedUsers =
    type === 'domain' ? domain.unAssginedUsers : project.unAssginedUsers;
  const defaultTypeValue =
    type === 'domain' ? domain.dropdownId : project.projectDropdownId;
  const billableValue = type === 'domain' ? domain.billable : project.billable;

  const [selectedAssignedList, setSelectedAssignedList] = useState<
    UserMappingItem[]
  >([]);

  const [availableAssignedList, setAvailableAssignedList] =
    useState<UserMappingItem[]>(defaultAssignedUsers);
  const [selectedUnassignedList, setSelectedUnassignedList] = useState<
    UserMappingItem[]
  >([]);

  const [availableUnassignedList, setAvailableUnassignedList] = useState<
    UserMappingItem[]
  >(defaultUnassignedUsers);

  const [billable, setBillable] = useState<boolean>(billableValue);
  const [typeValue, setTypeValue] = useState<ISelectItem | null>(
    defaultTypeValue
  );
  const [typeDropdown, setTypeDropdown] = useState<UserMappingItem[]>([]);
  const [role, setRole] = useState<ISelectItem | null>(project.role);
  const [securityGroupSelected, setSecurityGroupSelected] = useState<
    ISelectItem[]
  >(project.securityGroupDropdownIds);
  const [securityGroupItems, setSecurityGroupItems] = useState<
    ISecurityGroup[]
  >(project.securityGroupList);

  const [actionDisabled, setActionDisabled] = useState<boolean>(true);

  const {
    data: domainUsersData,
    isLoading: domainUserLoading,
    // error: domainUsersError,
  } = useDomainUsers(
    type === 'domain' && defaultTypeValue === null && !clearState
      ? (typeValue?.id as number)
      : undefined
  );

  const {
    data: projectUsersData,
    isLoading: projectUserLoading,
    // error: projectUsersError,
  } = useProjectUsers(
    type != 'domain' && defaultTypeValue === null && !clearState
      ? (typeValue?.id as number)
      : undefined
  );

  useEffect(() => {
    if (defaultTypeValue != null && typeValue != defaultTypeValue) {
      setBillable(false);
      if (type === 'domain') {
        dispatch(setClearTabData('domain'));
      } else {
        dispatch(setClearTabData('project'));
        setRole(null);
        setSecurityGroupSelected([]);
      }
    }
  }, [typeValue]);

  useEffect(() => {
    if (defaultTypeValue === null && typeValue != null && type === 'project') {
      setRole(null);
      setSecurityGroupSelected([]);
      setBillable(false);
    } else if (defaultTypeValue === null && typeValue != null) {
      setBillable(false);
    }
    setSelectedAssignedList([]);
    setSelectedUnassignedList([]);
  }, [typeValue]);

  useEffect(() => {
    if (!manageUsersData) return;

    setTypeDropdown(
      type === 'domain'
        ? manageUsersData?.domains
        : manageUsersData?.projectsList
    );
    if (defaultAssignedUsers.length === 0) {
      setAvailableUnassignedList(
        type === 'domain'
          ? manageUsersData?.unAssignedUsersForDomain
          : manageUsersData?.unAssignedUsers
      );
    }
  }, [manageUsersData]);

  useEffect(() => {
    if (
      domainUsersData &&
      type === 'domain' &&
      defaultAssignedUsers.length === 0
    ) {
      setAvailableUnassignedList(domainUsersData.unAssignedUsers);
      setAvailableAssignedList(domainUsersData.assignedUsers);
    } else if (
      projectUsersData &&
      type != 'domain' &&
      defaultAssignedUsers.length === 0
    ) {
      setAvailableUnassignedList(projectUsersData.unAssignedUsers);
      setAvailableAssignedList(projectUsersData.assignedUsers);
      setSecurityGroupItems(projectUsersData.securityGroupList ?? []);
    }
  }, [domainUsersData, projectUsersData]);

  useEffect(() => {
    if (type === 'domain') {
      setActionDisabled(!typeValue);
    } else if (typeValue && role) {
      if (role?.id === 1) {
        setActionDisabled(false);
      } else if (
        (role.id === 2 || role.id === 3) &&
        securityGroupSelected.length !== 0
      ) {
        setActionDisabled(false);
      } else {
        setActionDisabled(true);
      }
    } else {
      setActionDisabled(true);
    }
  }, [type, typeValue, role, securityGroupSelected]);

  const clearStateValues = () => {
    setRole(null);
    setSecurityGroupSelected([]);
    setBillable(false);
    setAvailableAssignedList([]);

    setAvailableUnassignedList(
      type === 'domain'
        ? (manageUsersData?.unAssignedUsersForDomain ?? [])
        : (manageUsersData?.unAssignedUsers ?? [])
    );
    setTypeValue(null);
  };

  useEffect(() => {
    if (clearState) {
      clearStateValues();
      dispatch(setClearState(false));
    }
  }, [clearState]);

  const moveValuesToStore = (
    assignedUsers: UserMappingItem[],
    unAssignedUsers: UserMappingItem[]
  ) => {
    if (type === 'project') {
      dispatch(setSecurityGroupDropdownIds(securityGroupSelected));
      dispatch(setRoleDropdownId(role));
      dispatch(setSecurityGroupList(securityGroupItems));
    }

    dispatch(
      setAssignedAndUnassignedUsers({type, assignedUsers, unAssignedUsers})
    );
    dispatch(setTypeDropdownId({type, value: typeValue}));
    dispatch(setBillableFlag({type, billable}));
  };

  const moveToAssigned = useCallback(() => {
    const transformedList: UserMappingItem[] = selectedUnassignedList.map(
      (item) =>
        transformAssignUsers(item, billable, role, securityGroupSelected, type)
    );
    const updatedSelectedFields: UserMappingItem[] = union(
      transformedList,
      availableAssignedList
    );

    const updatedUnassigned = differenceBy(
      availableUnassignedList,
      selectedUnassignedList,
      'key'
    );

    setSelectedUnassignedList([]);
    setAvailableAssignedList(updatedSelectedFields);
    setAvailableUnassignedList(updatedUnassigned);
    moveValuesToStore(updatedSelectedFields, updatedUnassigned);
  }, [
    selectedUnassignedList,
    availableAssignedList,
    billable,
    role,
    securityGroupSelected,
  ]);
  const moveToUnassigned = useCallback(() => {
    const transformUnassignUsersList: UserMappingItem[] =
      selectedAssignedList.map((item) => transformUnassignUsers(item));

    const updatedSelectedFields = union(
      availableUnassignedList,
      transformUnassignUsersList
    );
    const updatedAssigned = differenceBy(
      availableAssignedList,
      selectedAssignedList,
      'value'
    );
    setSelectedAssignedList([]);
    setAvailableUnassignedList(updatedSelectedFields);
    setAvailableAssignedList(updatedAssigned);
    moveValuesToStore(updatedAssigned, updatedSelectedFields);
  }, [availableUnassignedList, selectedAssignedList, billable]);
  const handleUnassignChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    user: KeyValue
  ) => {
    const isChecked = event.target.checked;

    setSelectedUnassignedList((prev) => {
      if (isChecked) {
        const alreadyExists = prev.some((u) => u.key === user.id);
        return alreadyExists
          ? prev
          : [...prev, {key: user.id, value: user.label, isSelected: false}];
      } else {
        return prev.filter((u) => u.key !== user.id);
      }
    });
  };

  const handleAssignChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    user: KeyValue
  ) => {
    const isChecked = event.target.checked;

    setSelectedAssignedList((prev) => {
      if (isChecked) {
        const alreadyExists = prev.some((u) => {
          const existingId = Number(u.value.split('|||')[0]);
          return existingId === user.id;
        });
        const foundItem = availableAssignedList.find(
          (item) => Number(item.value.split('|')[0]) === user?.id
        );
        const foundDomainItem = availableAssignedList.find(
          (item) => Number(item.value.split('||')[0]) === user?.id
        );

        return alreadyExists
          ? prev
          : [
              ...prev,
              {
                key: null,
                value:
                  type === 'domain'
                    ? (foundDomainItem?.value ?? '')
                    : (foundItem?.value ?? ''),
                isSelected: foundItem?.isSelected ?? false,
                text: user.label,
              },
            ];
      } else {
        return prev.filter((u) => {
          const existingId =
            type === 'domain'
              ? Number(u.value.split('|||')[0])
              : Number(u.value.split('|')[0]);
          return existingId !== user.id;
        });
      }
    });
  };

  const handleBillable = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setBillable(isChecked);
  };

  const changeRole = (role: ISelectItem | null) => {
    setRole(role);
    if (role?.id === '1') {
      setSecurityGroupSelected([]);
    }
  };

  const selectedUnassignedSortedList = useMemo(
    () =>
      selectedUnassignedList
        .map((item) => ({
          id: Number(item.key),
          label: item.value,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [selectedUnassignedList]
  );

  const availableUnassignedSortedList = useMemo(
    () =>
      availableUnassignedList
        .map((item) => ({
          id: Number(item.key),
          label: item.value,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [availableUnassignedList]
  );

  const selectedAssignedSortedList = useMemo(
    () =>
      selectedAssignedList
        .map((item) => ({
          id:
            type === 'domain'
              ? Number(item.value.split('|||')[0])
              : Number(item.value.split('|')[0]),
          label: item.value ?? '',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [selectedAssignedList, type]
  );

  const availableAssignedSortedList = useMemo(
    () =>
      availableAssignedList
        .map((item) => ({
          id:
            type === 'domain'
              ? Number(item.value.split('|||')[0])
              : Number(item.value.split('|')[0]),
          label: item.text ?? '',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [availableAssignedList, type]
  );

  return (
    <div
      className="content-container"
      data-testid={`assign-users-content-billable`}
    >
      <div
        className="content-title-container"
        data-testid={`assign-users-content-div`}
      >
        <Typography
          className="typo-text subhead4_size content-title"
          data-testid={`assign-users-content-title`}
        >
          {title}
        </Typography>
        <ArrSingleSelectDropdown
          dropdownLabel={
            type === t('userManagement.assignUsers.domain')
              ? t('userManagement.assignUsers.domainName')
              : t('userManagement.assignUsers.projectName')
          }
          dropdownItems={typeDropdown.map((item) => ({
            id: item.key ?? 0,
            name: item.value,
          }))}
          selectedValues={typeValue}
          onSelectionChange={setTypeValue}
          sizeVariant="large"
          dataTestId={
            type === t('userManagement.assignUsers.domain')
              ? 'asd-domain-dropdown'
              : 'asd-project-dropdown'
          }
          selectRestProps={{
            id: t('userManagement.assignUsers.domain')
              ? 'domain-dropdown'
              : 'project-dropdown',
            name: t('userManagement.assignUsers.domain')
              ? 'domain-dropdown'
              : 'project-dropdown',
          }}
          searchable
          showNoResultsText
        />
      </div>
      <div
        className="transfer-list-container"
        data-testid={`assign-users-content-unassign-users-list`}
      >
        <ArrTransferList
          showCheckbox={true}
          selectedList={selectedUnassignedSortedList}
          displayedList={availableUnassignedSortedList}
          onChange={handleUnassignChange}
          title={t('userManagement.assignUsers.unassignedUsers')}
          listItemContainerClass={
            type === t('userManagement.assignUsers.domain')
              ? 'domain-list-container'
              : ''
          }
          disable={!typeValue}
          isLoader={
            isLoader ||
            (type === 'domain' ? domainUserLoading : projectUserLoading)
          }
        />
        <div
          className={`transfer-action-container ${type === t('userManagement.assignUsers.project') ? 'project-action-tranfer' : ''}`}
          data-testid={`assign-users-content-assign-users-role-wrapper`}
        >
          {type === 'project' && (
            <div
              className="transfer-dropdown"
              data-testid={`assign-users-role-dropdown-wrapper`}
            >
              <ArrSingleSelectDropdown
                sizeVariant="medium"
                dropdownLabel={t('userManagement.assignUsers.role')}
                dropdownItems={
                  manageUsersData?.rolesList?.map((item) => ({
                    id: item.key ?? 0,
                    name: item.value,
                  })) ?? []
                }
                selectedValues={role}
                onSelectionChange={(values) => changeRole(values)}
                disabled={!typeValue || selectedUnassignedList.length === 0}
                dataTestId="asd-assign-user-role-dropdown"
                selectRestProps={{
                  id: 'role-dropdown',
                  name: 'role-dropdown',
                }}
                searchable
                showNoResultsText
              />
              <ArrMultiSelectDropdown
                dropdownLabel={t('userManagement.assignUsers.securityGroup')}
                dropdownItems={
                  securityGroupItems?.map((item) => ({
                    id: item.securityGroupId,
                    name: item.securityGroupLabel,
                  })) ?? []
                }
                selectedValues={securityGroupSelected}
                sizeVariant="medium"
                multiple
                onSelectionChange={setSecurityGroupSelected}
                withControls
                disabled={
                  !role || role.id === 1 || selectedUnassignedList.length === 0
                }
                withSelectAll={false}
                dataTestId="security-group-dropdown"
                checkboxDataTestId="assign-users-security-group-dropdown-checkbox"
                selectRestProps={{
                  id: 'security-group-dropdown',
                  name: 'security-group-dropdown',
                }}
              />
            </div>
          )}
          <div
            className={`transfer-button-container ${type === t('userManagement.assignUsers.project') ? 'project-transfer-button' : ''}`}
            data-testid={`assign-users-content-billable-wrapper`}
          >
            <div
              className="billable-button"
              data-testid={`assign-users-content-billable-link`}
            >
              <Typography
                className={`typo-text body4_size billable-text ${selectedUnassignedList.length != 0 && !actionDisabled && 'billable-text-active'}`}
                data-testid={`assign-users-content-billable-text`}
              >
                {t('userManagement.assignUsers.billable')}
              </Typography>
              <Checkbox
                data-testid="assign-users-billable-checkbox"
                checked={billable}
                onChange={handleBillable}
                className="list-item-checkbox"
                disabled={selectedUnassignedList.length === 0 || actionDisabled}
                icon={
                  selectedUnassignedList.length === 0 || actionDisabled ? (
                    <InactiveIcon />
                  ) : (
                    <UnCheckedIcon />
                  )
                }
                checkedIcon={<CheckedIcon />}
                id="billable"
                name="billable"
              />
            </div>
            <ArrIconButton
              ariaLabel="forward-icon"
              onClick={moveToAssigned}
              size="small"
              shape={
                selectedUnassignedList.length != 0 && !actionDisabled
                  ? 'default'
                  : 'none'
              }
              className="tranfer-action-icon-button tranfer-action-forward"
              icon={<ArrowForwardIcon className="arrow-icon" />}
              disabled={selectedUnassignedList.length === 0 || actionDisabled}
              testId="forward-icon"
              id="forward-icon"
              name="forward-icon"
            />
            <Divider className="move-buton-divider" />
            <ArrIconButton
              ariaLabel="back-icon"
              onClick={moveToUnassigned}
              disabled={selectedAssignedList.length === 0}
              size="small"
              shape={selectedAssignedList.length != 0 ? 'default' : 'none'}
              className="tranfer-action-icon-button"
              icon={<ArrowBackIcon className="arrow-icon" />}
              testId="back-icon"
              id="back-icon"
              name="back-icon"
            />
          </div>
        </div>
        <ArrTransferList
          showCheckbox={true}
          selectedList={selectedAssignedSortedList}
          displayedList={availableAssignedSortedList}
          onChange={handleAssignChange}
          checkboxDataTestId="assign-users-transfer-list-checkbox"
          title={t('userManagement.assignUsers.assignedUsers')}
          listItemContainerClass={
            type === t('userManagement.assignUsers.domain')
              ? 'domain-list-container'
              : ''
          }
          isLoader={type === 'domain' ? domainUserLoading : projectUserLoading}
        />
      </div>
    </div>
  );
};

export default AssignUsersContent;
